import React, { useState, useEffect } from "react";
import { X } from "lucide-react";

const SubcategoryForm = ({
  isOpen,
  onClose,
  onSubmit,
  initialData = null,
  categories,
  selectedCategory,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    image: null, // Changed field name to 'image'
    category: selectedCategory?.id || "",
  });

  const [previewImage, setPreviewImage] = useState(null);

  // Preload data if editing an existing subcategory
  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name,
        image: initialData.image || null, // Use 'image' here
        category: initialData.category?.id || selectedCategory?.id || "",
      });
      setPreviewImage(initialData.image || null);
    } else {
      setFormData({
        name: "",
        image: null, // Use 'image' here
        category: selectedCategory?.id || "",
      });
      setPreviewImage(null);
    }
  }, [initialData, isOpen, selectedCategory]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setFormData((prev) => ({
      ...prev,
      image: file, // Use 'image' here
    }));

    // Create image preview
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const submitData = new FormData();
    submitData.append("name", formData.name);
    submitData.append("category", formData.category);
    if (formData.image) {
      submitData.append("image", formData.image); // Use 'image' here
    }

    onSubmit(submitData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-96 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-600 hover:text-gray-900"
        >
          <X />
        </button>

        <h2 className="text-2xl font-bold mb-4">
          {selectedCategory
            ? `Add Subcategory to ${selectedCategory.name}`
            : "Add Subcategory"}
        </h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-700 mb-2">Subcategory Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border rounded-md"
              required
            />
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 mb-2">Category</label>
            <select
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border rounded-md"
              required
            >
              <option value="">Select a Category</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 mb-2">Image</label>
            <input
              type="file"
              onChange={handleImageChange} // Use 'handleImageChange'
              className="w-full px-3 py-2 border rounded-md"
            />
            {previewImage && (
              <div className="mt-2">
                <img
                  src={previewImage}
                  alt="Image Preview"
                  className="w-full h-32 object-cover rounded-md"
                />
              </div>
            )}
          </div>

          <button
            type="submit"
            className="w-full bg-blue-500 text-white py-2 rounded-md hover:bg-blue-600 transition"
          >
            Add Subcategory
          </button>
        </form>
      </div>
    </div>
  );
};

export default SubcategoryForm;
